<template>
  <div class="card">
    <div class="card-body">
      <form @submit.prevent="exportData" @reset.prevent="resetExport">
        <div class="mb-3">
          <div class="form-group">
            <select class="custom-select" v-model="exportType" :disabled="disableSelect">
              <option disabled selected value="">{{ translate("EXPORT.TYPES.CHOOSE") }}</option>
              <option :value="ExportType.PRODUCTS">{{ translate("EXPORT.TYPES.PRODUCTS") }}</option>
              <option :value="ExportType.CATEGORIES">{{ translate("EXPORT.TYPES.CATEGORIES") }}</option>
              <option :value="DiagnosticExportType.DIAGNOSTIC">{{ translate("EXPORT.TYPES.DIAGNOSTIC") }}</option>
            </select>
          </div>
        </div>

        <ExportStatusMessage
          v-if="displayStatusMessage"
          :exportType="exportType"
          :successEntriesCount="entriesCount"
          :errorMessage="errorMessage"
          @downloadFile="downloadFile"
          @restartExport="restartSameExport"
        />

        <div class="row pt-2">
          <div class="col-12 d-flex justify-content-between align-items-center">
            <template>
              <button v-if="displayCancelButton" type="button" class="btn btn-white-cx" @click="cancelExport">
                {{ translate("EXPORT.BUTTONS.CANCEL") }}
              </button>

              <router-link
                v-if="displayGoToDashboardButton"
                :to="{ name: dashboardRoute }"
                class="btn btn-white-cx"
                @click="resetExport"
              >
                {{ translate("EXPORT.BUTTONS.GO_TO_DASHBOARD") }}
              </router-link>
            </template>

            <div class="ml-auto">
              <button
                v-if="displayExportButton"
                class="btn btn-primary-cx ml-2"
                type="submit"
                :disabled="disableExportButton"
              >
                {{ translate("EXPORT.BUTTONS.EXPORT") }}
              </button>

              <button v-if="displayLoadingExportButton" class="btn btn-primary-cx disabled ml-2" disabled>
                <span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>

                {{ translate("EXPORT.BUTTONS.EXPORTING") }}
              </button>
            </div>

            <div>
              <button v-if="displayNewExportButton" class="btn btn-secondary-cx" type="reset">
                {{ translate("EXPORT.BUTTONS.NEW_EXPORT") }}
              </button>

              <button v-if="displayRestartSameExportButton" class="btn btn-primary-cx ml-2" @click="restartSameExport">
                {{ translate("EXPORT.BUTTONS.RESTART") }}
              </button>

              <button
                v-if="displayDownloadFileButton"
                class="btn btn-primary-cx ml-2"
                type="button"
                @click="downloadFile"
              >
                {{ translate("EXPORT.BUTTONS.DOWNLOAD") }}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck
// TODO: This should be removed & types should be fixed in https://recommerce.atlassian.net/browse/CXCORE-7888
import { computed, onMounted, ref } from "vue";
import ExportStatus from "@/apps/dashboard/catalog/core/domain/ExportStatus.enum";
import ExportType from "@/apps/dashboard/catalog/core/domain/ExportType.enum";
import DiagnosticExportType from "@/apps/dashboard/buybackDiagnostic/core/domain/ExportType.enum";
import { getExportCsv, startExport } from "@/apps/dashboard/catalog/core/infrastructure/service/exportService";
import {
  getExportCsv as diagnosticGetExportCsv,
  startExport as diagnosticStartExport,
} from "@/apps/dashboard/buybackDiagnostic/core/infrastructure/service/exportService";
import { routes } from "@/routing/routes";
import { useRoute } from "vue-router";
import ExportStatusMessage from "@/apps/dashboard/catalog/view/components/Export/ExportStatusMessage.vue";
import formattingUtils from "@/core/utils/formatting.utils";
import organizationService from "@/core/services/api/organization.service";
import useTranslate from "@/core/composables/translate";
import useBreadcrumbStore from "@/core/store/config/breadcrumbsStore";

const accountId = ref<string | null>(null);
const exportType = ref<string>("");
const exportStatus = ref<ExportStatus | null>(null);
const entriesCount = ref<number | null>(null);
const errorMessage = ref<string | null>(null);
const exportUrl = ref<string | null>(null);
const { translate } = useTranslate();
const dashboardRoute = routes.home.name;

const displayExportButton = computed(() => null === exportStatus.value);
const disableExportButton = computed(() => "" === exportType.value);
const displayLoadingExportButton = computed(() => ExportStatus.IN_PROGRESS === exportStatus.value);
const displayCancelButton = computed(() => ExportStatus.IN_PROGRESS === exportStatus.value);
const displayStatusMessage = computed(
  () => exportStatus.value && [ExportStatus.SUCCESS, ExportStatus.ERROR].includes(exportStatus.value)
);
const displayNewExportButton = computed(
  () => exportStatus.value && [ExportStatus.SUCCESS, ExportStatus.ERROR].includes(exportStatus.value)
);
const displayGoToDashboardButton = computed(
  () => exportStatus.value && [ExportStatus.SUCCESS, ExportStatus.ERROR].includes(exportStatus.value)
);
const displayRestartSameExportButton = computed(() => ExportStatus.ERROR === exportStatus.value);
const displayDownloadFileButton = computed(() => ExportStatus.SUCCESS === exportStatus.value);

const disableSelect = computed(() => exportStatus.value !== null);

const route = useRoute();
const breadcrumbStore = useBreadcrumbStore();

onMounted(async () => {
  accountId.value = route.params.accountId ?? null;
  const organization = await organizationService.fetchPublicDetailsOrganizationById(accountId.value);

  breadcrumbStore.setBreadcrumb([
    {
      title: translate("EXPORT.TITLE", { organizationName: organization.commonName }),
      bc_icon: translate("EXPORT.ICON"),
    },
  ]);
});

const exportData = async () => {
  try {
    exportStatus.value = ExportStatus.IN_PROGRESS;

    if (Object.values(DiagnosticExportType).includes(exportType.value as DiagnosticExportType)) {
      await exportDiagnosticData();
    } else {
      await exportCatalogData();
    }

    exportStatus.value = ExportStatus.SUCCESS;
  } catch (error) {
    exportStatus.value = ExportStatus.ERROR;
    errorMessage.value = String(error);
  }
};

const exportDiagnosticData = async () => {
  const exportCsv = await diagnosticStartExport(exportType.value as DiagnosticExportType, accountId.value!);
  const finishedExportCsv = await diagnosticGetExportCsv(exportCsv);
  exportUrl.value = finishedExportCsv.url;
  entriesCount.value = finishedExportCsv.entriesCount;
  errorMessage.value = finishedExportCsv.error;
};

const exportCatalogData = async () => {
  const exportCsv = await startExport(exportType.value as ExportType, accountId.value!);
  const finishedExportCsv = await getExportCsv(exportCsv);
  exportUrl.value = finishedExportCsv.url;
  entriesCount.value = finishedExportCsv.entriesCount;
  errorMessage.value = finishedExportCsv.error;
};

const resetExport = () => {
  exportStatus.value = null;
  entriesCount.value = null;
  errorMessage.value = null;
};

const downloadFile = () => {
  const link = document.createElement("a");
  link.href = exportUrl.value!;
  link.download = `${exportType.value}-${accountId.value}-${formattingUtils.formatDateTime(new Date())}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

const restartSameExport = () => {
  resetExport();
  exportData();
};

const cancelExport = () => window.location.reload();
</script>

<style scoped lang="scss">
@import "@/designSystem/style/variables/_colors.scss";

select[disabled] {
  color: #3f4254;
  background-color: #fff;
  cursor: not-allowed;
}

.btn-primary-cx {
  color: $white;
  background-color: $cx-base-color;
  border-color: $cx-base-color;

  &:hover {
    color: $white;
    background-color: $cx-base-color;
    border-color: $cx-base-color;
  }

  &:disabled {
    cursor: not-allowed;
    pointer-events: all !important;
  }

  &.disabled {
    background-color: $cx-light-base-color;
    border-color: $cx-light-base-color;
    color: $cx-base-color;
  }
}

.btn-secondary-cx {
  color: $cx-base-color;
  background-color: $white;
  border-color: $cx-base-color;

  &:hover {
    color: $white;
    background-color: $cx-base-color;
    border-color: $white;
  }
}

.btn-white-cx {
  color: $gray-600;
  background-color: white;
  border-color: $gray-600;
}
</style>
